import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { AuthLayout } from "@/components/catalyst/AuthLayout";
import { Button } from "@/components/catalyst/Button";
import {
	DescriptionDetails,
	DescriptionList,
	DescriptionTerm,
} from "@/components/catalyst/DescriptionList";
import { Heading, Subheading } from "@/components/catalyst/Heading";
import { Logo } from "@/components/Logo";
import { orpc } from "@/orpc/client";

type Status =
	| "idle"
	| "loading"
	| "review"
	| "confirming"
	| "terminated"
	| "error";

export function ReferralLinkTermination() {
	const [status, setStatus] = useState<Status>("idle");
	const [errorMsg, setErrorMsg] = useState("");

	const searchParams = useSearch({ from: "/partner" });
	const linkId = searchParams.linkId;
	const token = searchParams.terminationToken;

	const { data, isLoading, error } = useQuery(
		orpc.publicReferralLinkTermination.getDetails.queryOptions({
			input: linkId && token ? { linkId, token } : skipToken,
		}),
	);

	const confirmMutation = useMutation(
		orpc.publicReferralLinkTermination.confirm.mutationOptions({
			onSuccess: () => {
				setStatus("terminated");
			},
			onError: (err) => {
				setStatus("error");
				setErrorMsg(err.message || "Failed to terminate");
			},
		}),
	);

	useEffect(() => {
		if (!linkId || !token) {
			setStatus("error");
			setErrorMsg(
				"Invalid link. Please check your email for the correct link.",
			);
			return;
		}

		if (isLoading) {
			setStatus("loading");
		} else if (error) {
			setStatus("error");
			setErrorMsg(error.message || "Something went wrong");
		} else if (data?.alreadyTerminated) {
			setStatus("terminated");
		} else if (data) {
			setStatus("review");
		}
	}, [linkId, token, isLoading, error, data]);

	const handleConfirm = () => {
		if (!linkId || !token) return;
		setStatus("confirming");
		confirmMutation.mutate({ linkId, token });
	};

	function renderContent() {
		if (status === "idle" || status === "loading") {
			return (
				<>
					<Heading>Loading...</Heading>
					<Subheading>Fetching termination details</Subheading>
					<Loader2 className="block h-8 w-8 animate-spin text-base-content" />
				</>
			);
		}

		if (status === "error") {
			return (
				<>
					<div className="mx-auto w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mb-4">
						<XCircle className="size-6 text-error" />
					</div>
					<Heading>Something went wrong</Heading>
					<Subheading className="text-error">{errorMsg}</Subheading>
				</>
			);
		}

		if (status === "terminated") {
			return (
				<div className="max-w-sm">
					<div className="mx-auto w-12 h-12 rounded-full bg-success/10 flex items-center justify-center mb-4">
						<CheckCircle className="size-6 text-success" />
					</div>
					<Heading>Agreement Terminated</Heading>
					<Subheading>
						The referral link for {data?.link.appName} has been terminated. You
						will still receive any outstanding payouts for existing
						attributions.
					</Subheading>
				</div>
			);
		}

		if (status === "review" || status === "confirming") {
			const link = data?.link;
			const payoutPercent = link ? (link.payoutRateBps / 100).toFixed(2) : "0";
			const duration = link?.payoutDurationDays
				? `${link.payoutDurationDays} days`
				: "Lifetime";
			const expiry = link?.expiresAt
				? new Date(link.expiresAt).toLocaleDateString()
				: "Never";
			const minPayout = link?.minPayoutCents
				? `$${(link.minPayoutCents / 100).toFixed(2)}`
				: "None";

			return (
				<>
					<Heading>Terminate Referral Agreement</Heading>
					<Subheading>
						The owner of {link?.appName} has requested to terminate this
						referral link agreement. Please review the details below.
					</Subheading>

					<DescriptionList className="mt-6 w-full max-w-md">
						<DescriptionTerm>App</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{link?.appName}
						</DescriptionDetails>

						<DescriptionTerm>Payout Rate</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{payoutPercent}% of revenue
						</DescriptionDetails>

						<DescriptionTerm>Payout Duration</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{duration}
						</DescriptionDetails>

						<DescriptionTerm>Link Expires</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{expiry}
						</DescriptionDetails>

						<DescriptionTerm>Minimum Payout</DescriptionTerm>
						<DescriptionDetails className="text-right">
							{minPayout}
						</DescriptionDetails>
					</DescriptionList>

					<div className="mt-6 w-full max-w-md">
						<Button
							className="w-full"
							color="red"
							onClick={handleConfirm}
							disabled={status === "confirming"}
						>
							{status === "confirming" ? (
								<>
									<Loader2 className="size-4 animate-spin mr-2" />
									Terminating...
								</>
							) : (
								"Agree to Terminate"
							)}
						</Button>
						<p className="mt-3 text-sm text-base-content/60 text-center">
							By confirming, this referral link will be permanently deactivated.
							Outstanding payouts for existing attributions will still be
							honored.
						</p>
					</div>
				</>
			);
		}

		return null;
	}

	return (
		<AuthLayout>
			<div className="flex flex-col gap-6 w-full max-w-2xl">
				<div className="flex flex-col items-center gap-4">
					<Logo />
					{renderContent()}
				</div>
			</div>
		</AuthLayout>
	);
}
